import React from "react";
import { useTranslation } from "react-i18next";
import "./AboutExpertise.css";

/**
 * UniTech Engineering — À propos, section 04 « Expertise & Preuves ».
 * Les cartes électronique et bancs utilisent les images fournies.
 * Les images ne sont pas des preuves de résultats de validation.
 * La capture OPENlift provient du média produit précédemment fourni.
 * Aucun chiffre, certificat ou résultat de test n'est inventé.
 */
const TEXT = {
  fr: {
    eyebrow: "NOTRE EXPERTISE EN ACTION",
    titleFirst: "Nous concevons.",
    titleAccent: "Nous développons.",
    titleLast: "Nous testons.",
    lead: "Un savoir-faire transversal, de l’électronique aux plateformes connectées, avec des vérifications adaptées aux équipements.",
    imagePending: "Photo authentique à intégrer",
    sampleMedia: "Capture produit · données d’exemple",
    cards: [
      {
        id: "electronics",
        title: "Électronique & embarqué",
        detail: "Cartes, interfaces et logiciels conçus pour communiquer avec les équipements.",
        alt: "Poste de développement électronique avec deux moniteurs, une carte et un oscilloscope"
      },
      {
        id: "validation",
        title: "Bancs d’essai & validation",
        detail: "Essais documentés et observation du comportement des contrôleurs.",
        alt: "Bancs de test de contrôleurs d’ascenseurs disposés sur deux niveaux dans un laboratoire"
      },
      {
        id: "platform",
        title: "Plateformes connectées",
        detail: "Services, API et interfaces permettant une lecture utile des informations.",
        alt: "Aperçu du tableau de bord et de l'application OPENlift avec données d'exemple"
      }
    ],
    disclaimer: "Avant publication : utiliser des photographies réelles autorisées, des légendes exactes et des périmètres d’essai vérifiés."
  },
  en: {
    eyebrow: "OUR EXPERTISE IN ACTION",
    titleFirst: "We design.",
    titleAccent: "We develop.",
    titleLast: "We test.",
    lead: "Expertise spanning electronics and connected platforms, with verification tailored to each equipment configuration.",
    imagePending: "Authentic photograph to be added",
    sampleMedia: "Product preview · example data",
    cards: [
      {
        id: "electronics",
        title: "Electronics & embedded systems",
        detail: "Boards, interfaces and software designed to communicate with equipment.",
        alt: "Electronics workbench with two monitors, a circuit board and an oscilloscope"
      },
      {
        id: "validation",
        title: "Test benches & validation",
        detail: "Documented testing and observation of controller behavior.",
        alt: "Elevator controller test benches arranged on two levels in a laboratory"
      },
      {
        id: "platform",
        title: "Connected platforms",
        detail: "Services, APIs and interfaces that help turn information into useful insights.",
        alt: "Preview of OPENlift dashboard and mobile app with example data"
      }
    ],
    disclaimer: "Before publication: use authorized real photographs, accurate captions and verified test scopes."
  }
};

// Images provided for integration; publication rights should be confirmed.
// The platform image already exists in the previously validated solution pack.
const VERIFIED_MEDIA = {
  electronics: "/images/hardware.png",
  validation: "/images/Bancs.png",
  platform: "/images/about/solutions/openlift-dashboard.webp"
};

function TechnicalPlaceholder({ kind }) {
  if (kind === "electronics") {
    return (
      <svg className="about-expertise__diagram" viewBox="0 0 240 142" fill="none" aria-hidden="true" focusable="false">
        <rect x="51" y="27" width="138" height="88" rx="9" stroke="currentColor" strokeWidth="2.2"/>
        <rect x="91" y="47" width="58" height="48" rx="5" stroke="currentColor" strokeWidth="2.5"/>
        <path d="M91 58H68V44H58M91 80H73v23H59m90-44h23V44h13m-36 36h23v23h13M105 47V35m18 12V35m13 12V35m-31 60v12m18-12v12m13-12v12" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="58" cy="44" r="4" fill="currentColor"/><circle cx="59" cy="103" r="4" fill="currentColor"/><circle cx="185" cy="44" r="4" fill="currentColor"/><circle cx="185" cy="103" r="4" fill="currentColor"/>
        <path d="M102 71h35M120 54v34" stroke="currentColor" strokeWidth="1.5" opacity=".5"/>
      </svg>
    );
  }
  return (
    <svg className="about-expertise__diagram" viewBox="0 0 240 142" fill="none" aria-hidden="true" focusable="false">
      <rect x="35" y="27" width="170" height="90" rx="9" stroke="currentColor" strokeWidth="2.2"/>
      <rect x="49" y="42" width="58" height="57" rx="4" stroke="currentColor" strokeWidth="2"/>
      <path d="M78 52v37M61 52h34m-34 37h34" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <rect x="125" y="42" width="66" height="57" rx="4" stroke="currentColor" strokeWidth="2"/>
      <path d="M138 77h10l7-21 10 29 6-14h8" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="68" cy="111" r="2.5" fill="currentColor"/><circle cx="80" cy="111" r="2.5" fill="currentColor"/>
      <path d="M101 127h38" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
}

function ExpertiseCard({ card, copy }) {
  const photo = VERIFIED_MEDIA[card.id];
  const isProduct = card.id === "platform";
  return (
    <article className={`about-expertise__card about-expertise__card--${card.id}`}>
      <div className="about-expertise__media">
        {photo ? (
          <img
            className="about-expertise__image"
            src={photo}
            alt={card.alt}
            loading="lazy"
            decoding="async"
            width="720"
            height="440"
          />
        ) : (
          <div className="about-expertise__placeholder" role="img" aria-label={card.alt}>
            <TechnicalPlaceholder kind={card.id} />
          </div>
        )}
      </div>
      <div className="about-expertise__card-content">
        <h3 className="about-expertise__card-title">{card.title}</h3>
        <p className="about-expertise__card-description">{card.detail}</p>
      </div>
    </article>
  );
}

export default function AboutExpertise() {
  const { i18n } = useTranslation();
  const language = (i18n.resolvedLanguage || i18n.language || "fr")
    .split("-")[0].toLowerCase();
  const copy = TEXT[language] || TEXT.fr;

  return (
    <section id="about-expertise" className="about-expertise" aria-labelledby="about-expertise-title">
      <div className="about-expertise__container">
        <p className="about-expertise__eyebrow">{copy.eyebrow}</p>
        <header className="about-expertise__header">
          <h2 id="about-expertise-title" className="about-expertise__title">
            <span className="about-expertise__title-line">{copy.titleFirst}</span>
            <span className="about-expertise__title-line">
              <span className="about-expertise__title-accent">{copy.titleAccent}</span>
              {" "}{copy.titleLast}
            </span>
          </h2>
          <p className="about-expertise__intro">{copy.lead}</p>
        </header>

        <div className="about-expertise__grid">
          {copy.cards.map((card) => <ExpertiseCard key={card.id} card={card} copy={copy} />)}
        </div>
      </div>
    </section>
  );
}
